import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled, { createGlobalStyle } from 'styled-components'
import { getPermit, exportPermitPdf } from 'services/permitService'
import { downloadBlob } from 'utils/downloadBlob'

const fmt = iso => iso ? new Date(iso).toLocaleString('en-GB', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '—'

const PermitPrintView = () => {
  const { bpId, permitId } = useParams()
  const [permit, setPermit] = useState(null)
  const [error, setError] = useState(null)
  const [downloading, setDownloading] = useState(false)

  useEffect(() => {
    getPermit(bpId, permitId)
      .then(res => setPermit(res?.data?.data || null))
      .catch(() => setError('Could not load this permit.'))
  }, [bpId, permitId])

  const handleDownload = async () => {
    setDownloading(true)
    try {
      const res = await exportPermitPdf(bpId, permitId)
      downloadBlob(res?.data, `permit-${permit?.reference || permitId}.pdf`)
    } catch (e) {
      toast.error('Could not generate the permit PDF. Please try again.')
    } finally {
      setDownloading(false)
    }
  }

  if (error) return <Sheet><p>{error}</p></Sheet>
  if (!permit) return <Sheet><p>Loading permit…</p></Sheet>

  const building = permit.building || {}
  const address = [building.street, building.city, building.postalCode].filter(Boolean).join(', ')

  return (
    <>
      <PrintStyles />
      <Toolbar className='no-print'>
        <PrintBtn onClick={handleDownload} disabled={downloading}>
          {downloading ? 'Preparing PDF…' : 'Download PDF'}
        </PrintBtn>
      </Toolbar>
      <Sheet>
        <SheetHead>
          <div>
            <SheetEyebrow>Permit to work</SheetEyebrow>
            <SheetRef>{permit.reference}</SheetRef>
          </div>
          <StatusBox $status={permit.status}>{permit.status?.replace(/_/g, ' ').toUpperCase()}</StatusBox>
        </SheetHead>

        <SectionTitle>Work details</SectionTitle>
        <Table>
          <tbody>
            <tr><Th>Title</Th><Td>{permit.title}</Td></tr>
            <tr><Th>Activity</Th><Td>{permit.activityTypeLabel}{permit.isHighRisk ? ' — HIGH RISK' : ''}</Td></tr>
            <tr><Th>Building</Th><Td>{building.name}{address ? ` — ${address}` : ''}</Td></tr>
            <tr><Th>Validity</Th><Td>{fmt(permit.validFrom)} → {fmt(permit.validTo)}</Td></tr>
            {permit.description && <tr><Th>Scope &amp; precautions</Th><Td>{permit.description}</Td></tr>}
          </tbody>
        </Table>

        <SectionTitle>Contractor</SectionTitle>
        <Table>
          <tbody>
            <tr><Th>Name</Th><Td>{permit.contractor?.name || 'Internal / none'}</Td></tr>
            {permit.contractor?.company && <tr><Th>Company</Th><Td>{permit.contractor.company}</Td></tr>}
            {permit.contractor?.phone && <tr><Th>Phone</Th><Td>{permit.contractor.phone}</Td></tr>}
            <tr><Th>RAMS</Th><Td>
              {permit.ramsDocument
                ? `${permit.ramsDocument.displayName}${permit.ramsDocument.expiresAt ? ` (valid to ${permit.ramsDocument.expiresAt})` : ''}`
                : 'Not linked'}
            </Td></tr>
          </tbody>
        </Table>

        <SectionTitle>Authorisation</SectionTitle>
        <SignatureGrid>
          <SignatureBox>
            <SigRole>Requested by</SigRole>
            <SigName>{permit.requestedBy || '—'}</SigName>
            <SigTime>{fmt(permit.createdAt)}</SigTime>
          </SignatureBox>
          <SignatureBox>
            <SigRole>Approved by (issuer)</SigRole>
            <SigName>{permit.approvedBy || '—'}</SigName>
            <SigTime>{fmt(permit.approvedAt)}</SigTime>
          </SignatureBox>
          <SignatureBox>
            <SigRole>Accepted by (worker)</SigRole>
            <SigName>{permit.acceptedByName || '—'}</SigName>
            <SigTime>{fmt(permit.acceptedAt)}</SigTime>
          </SignatureBox>
          <SignatureBox>
            <SigRole>Closed by</SigRole>
            <SigName>{permit.closedBy || '—'}</SigName>
            <SigTime>{fmt(permit.closedAt)}</SigTime>
          </SignatureBox>
        </SignatureGrid>
        {permit.closureNote && (
          <ClosureBlock>
            <SectionTitle>Close-out note</SectionTitle>
            <p>{permit.closureNote}</p>
          </ClosureBlock>
        )}

        <SectionTitle>Audit trail</SectionTitle>
        <Table>
          <tbody>
            {(permit.events || []).map((e, i) => (
              <tr key={i}>
                <Th style={{ width: 170 }}>{fmt(e.createdAt)}</Th>
                <Td>
                  <strong style={{ textTransform: 'capitalize' }}>{e.event?.replace(/_/g, ' ')}</strong>
                  {e.user ? ` — ${e.user}` : ' — system'}
                  {e.note ? `. ${e.note}` : ''}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>

        <Footnote>
          This permit authorises only the work described, within the stated validity window, subject to the
          linked RAMS and the conditions accepted above. Display at the work area. Generated {fmt(new Date().toISOString())}.
        </Footnote>
      </Sheet>
    </>
  )
}

export default PermitPrintView

const PrintStyles = createGlobalStyle`
  @media print {
    .no-print { display: none !important; }
    body { background: #fff !important; }
  }
`

const Toolbar = styled.div`
  display: flex; justify-content: flex-end;
  max-width: 800px;
  margin: 16px auto 0;
  padding: 0 16px;
`

const PrintBtn = styled.button`
  font: inherit;
  padding: 9px 18px;
  background: #0C1229;
  color: #fff;
  border: none;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 800;
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: default; }
`

const Sheet = styled.div`
  max-width: 800px;
  margin: 12px auto 48px;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 12px;
  padding: 36px 40px;
  font-size: 13px;
  color: #0C1229;
  @media (max-width: 600px) { padding: 20px 16px; margin: 8px 8px 32px; }
  @media print {
    border: none;
    margin: 0;
    border-radius: 0;
    max-width: none;
  }
`

const SheetHead = styled.header`
  display: flex; align-items: flex-start; justify-content: space-between;
  border-bottom: 3px solid #0C1229;
  padding-bottom: 16px;
  margin-bottom: 20px;
`
const SheetEyebrow = styled.div`
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-weight: 800;
  color: #6b7280;
`
const SheetRef = styled.div`
  font-family: monospace;
  font-size: 30px;
  font-weight: 700;
  letter-spacing: 0.02em;
`
const StatusBox = styled.div`
  padding: 8px 16px;
  border: 2px solid ${({ $status }) =>
    $status === 'active' ? '#15803d'
    : $status === 'expired' || $status === 'cancelled' ? '#b00020'
    : $status === 'suspended' ? '#d97706'
    : '#0C1229'};
  color: ${({ $status }) =>
    $status === 'active' ? '#15803d'
    : $status === 'expired' || $status === 'cancelled' ? '#b00020'
    : $status === 'suspended' ? '#d97706'
    : '#0C1229'};
  font-weight: 800;
  font-size: 14px;
  letter-spacing: 0.08em;
`

const SectionTitle = styled.h2`
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 800;
  margin: 22px 0 8px;
  color: #0C1229;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  tr { border-bottom: 1px solid #ececec; }
`
const Th = styled.td`
  padding: 7px 12px 7px 0;
  font-weight: 700;
  width: 200px;
  vertical-align: top;
  color: #475569;
`
const Td = styled.td`
  padding: 7px 0;
  vertical-align: top;
`

const SignatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  @media screen and (max-width: 640px) { grid-template-columns: repeat(2, 1fr); }
`
const SignatureBox = styled.div`
  border: 1px solid #d6d6da;
  border-radius: 8px;
  padding: 12px;
  min-height: 84px;
`
const SigRole = styled.div`
  font-size: 9.5px;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  font-weight: 800;
  color: #6b7280;
  margin-bottom: 8px;
`
const SigName = styled.div`
  font-weight: 700;
  font-size: 13.5px;
`
const SigTime = styled.div`
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
`

const ClosureBlock = styled.div`
  p { margin: 0; }
`

const Footnote = styled.p`
  margin-top: 28px;
  font-size: 10.5px;
  color: #6b7280;
  border-top: 1px solid #ececec;
  padding-top: 12px;
  line-height: 1.5;
`
